import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import {
  setUpgradeDialog,
  UpgradeDialogType,
  UpgradeDialogTypeMap
} from "@/reducers/slices/dialogSlice/dialogSlice";
import {
  generalDateStringForClientSide,
  getRelativeTime,
  returnDateString
} from "@/utils/time";
import {
  CheckCircleRounded,
  ErrorRounded,
  MailRounded,
  WatchLaterRounded
} from "@mui/icons-material";
import { Box, Button, Stack, Typography, styled } from "@mui/material";
import React, { useMemo } from "react";
import { trackMixpanelEvent } from "@/utils/mixpanel";
import { MIXPANEL_EVENTS } from "@/constants/mixpanel";
import { PLANS_CATEGORY } from "@/features/pricing/types";
import useContactSales from "@/hooks/useContactSales";

const Wrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  width: "100%",
  textAlign: "center",
  gap: theme.spacing(0.5),
  justifyContent: "center",
  alignItems: "center"
}));

export const DubStatusContent = (): React.ReactNode => {
  const dispatch = useAppDispatch();

  const dueBy = useTypedSelector((state) => state.dubPreview.dubDetails.dueBy);
  const dubStatus = useTypedSelector(
    (state) => state.dubPreview.dubDetails.dubStatus
  );
  const completedAt = useTypedSelector(
    (state) => state.dubPreview.dubDetails.completedAt
  );

  const isDueByPassed = useMemo(
    () => Boolean(!dueBy || Date.now() > new Date(dueBy).valueOf()),
    [dueBy]
  );
  const isIcpUser = useTypedSelector((state) => state.globalState.isICPUser);

  const { dubVersion, qcDubVersions, qcEnabled } = useTypedSelector(
    (state) => state.dubPreview.dubDetails
  );
  const isWatermarked = useTypedSelector(
    (state) => !state.workspace?.workspacePlan?.planDetails?.noWaterMark
  );

  const userCurrentPlan = useTypedSelector(
    (state) => state.workspace.workspacePlan.planDetails
  );

  const isEnterprise = userCurrentPlan?.category === PLANS_CATEGORY.CUSTOM;

  const estimatedDeliveryComponent = useMemo(() => {
    const isNonQAVersion =
      !qcEnabled ||
      (qcEnabled &&
        dubVersion &&
        qcDubVersions &&
        !qcDubVersions.includes(dubVersion));

    if (isNonQAVersion) {
      return (
        <>
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled"
            }}
          >
            Estimated time of completion
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary"
            }}
          >
            {getRelativeTime(dueBy as Date)}
          </Typography>
        </>
      );
    }

    return (
      <>
        <Typography
          variant="caption"
          sx={{
            color: "text.disabled"
          }}
        >
          Estimated delivery by
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary"
          }}
        >
          {returnDateString(dueBy as Date, undefined, {
            year: "numeric",
            month: "long",
            day: "numeric"
          })}
        </Typography>
      </>
    );
  }, [dubVersion, dueBy, qcDubVersions, qcEnabled]);

  const showUpgradePrompt = () => {
    dispatch(
      setUpgradeDialog({
        open: true,
        type: UpgradeDialogType.REMOVE_WATERMARK
      })
    );
    trackMixpanelEvent(MIXPANEL_EVENTS.UP_SELL_CLICKED, {
      "Entry Point": UpgradeDialogTypeMap[UpgradeDialogType.REMOVE_WATERMARK]
    });
  };

  const { onContactSales } = useContactSales();

  if (dubStatus === "FAILED" && isDueByPassed) {
    return (
      <Wrapper>
        <Stack gap={1} justifyContent={"center"} alignItems={"center"}>
          <ErrorRounded
            color="error"
            sx={{
              height: 36,
              width: 36
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "error.main"
            }}
          >
            Error in processing
          </Typography>
          <Typography
            variant="caption"
            color={"text.primary"}
            textAlign={"center"}
          >
            We are unable to process the dub right now, please contact support
          </Typography>
        </Stack>
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            // href={`mailto:support.murfdub@murf.ai?subject=${encodeURI(
            //   "My dub has failed"
            // )}&body=${encodeURI(
            //   `My dub for project: ${projectId}, fileId: ${fileId} and dub: ${dubId} has failed.\nPlease look into this.`
            // )}`}
            // target="_blank"
            color="secondary"
            startIcon={<MailRounded sx={{ height: 20, width: 20 }} />}
            size="medium"
            onClick={() => {
              window.HubSpotConversations
                ? window.HubSpotConversations.widget.open()
                : null;
            }}
          >
            <Typography variant="caption" color={"text.secondary"}>
              Contact support
            </Typography>
          </Button>
        </Box>
      </Wrapper>
    );
  }

  if (dubStatus === "COMPLETED") {
    return (
      <>
        <Wrapper>
          <CheckCircleRounded
            color="success"
            sx={{
              height: 36,
              width: 36
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "success.main"
            }}
          >
            {qcEnabled ? "Delivered" : "Processing finished"}
          </Typography>
          {completedAt ? (
            <Typography mt={0.5} variant="body2" color={"text.secondary"}>
              Finished on{" "}
              {generalDateStringForClientSide(new Date(completedAt))}
            </Typography>
          ) : null}
        </Wrapper>
        {isWatermarked ? (
          <Button
            variant="contained"
            color={isIcpUser ? "secondary" : "primary"}
            size="large"
            onClick={showUpgradePrompt}
          >
            Remove watermark
          </Button>
        ) : null}
        {!isEnterprise ? (
          <Button
            variant="contained"
            size="large"
            color={isIcpUser ? "primary" : "secondary"}
            onClick={() => {
              onContactSales("Dub Preview Page");
            }}
          >
            Contact Sales
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <Wrapper>
      <WatchLaterRounded
        color="info"
        sx={{
          height: 36,
          width: 36,
          mb: 0.5
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: "info.main"
        }}
      >
        Processing changes
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: "text.primary"
        }}
      >
        We are processing your changes to deliver the dub
      </Typography>
      <Stack
        direction={"row"}
        gap={0.5}
        textAlign={"center"}
        justifyContent={"center"}
      >
        {dueBy && Date.now() < new Date(dueBy).valueOf() ? (
          estimatedDeliveryComponent
        ) : (
          <Typography
            variant="caption"
            sx={{
              color: "text.disabled"
            }}
          >
            Delayed. We'll notify you via email.
          </Typography>
        )}
      </Stack>
    </Wrapper>
  );
};
